import React, { useEffect } from 'react';
import { MessagePropertiesContext } from '../../../../contexts/messageProperties';
import { getUniqueRadioValue } from '../reserveCollectUtils';
import { ReserveCollectSearchResultStore } from '../../../../types';

interface ReserveCollectStoreProps {
  store: ReserveCollectSearchResultStore;
  disabled: boolean;
  searchProductCode: string;
  isRingProduct: boolean;
  selectedOption: string;
  setSelectedOption: (option: string) => void;
  setSelectedStore: React.Dispatch<React.SetStateAction<ReserveCollectSearchResultStore | undefined>>;
  priceUponEnquiry?: boolean;
}

function ReserveCollectStore({
  store,
  disabled,
  searchProductCode,
  isRingProduct,
  selectedOption,
  setSelectedOption,
  setSelectedStore,
  priceUponEnquiry
}: ReserveCollectStoreProps): React.ReactElement {
  const instoreUniqueValue = getUniqueRadioValue(store, 'instore');
  const onlineUniqueValue = getUniqueRadioValue(store, 'online');
  const [formattedMessage] = React.useContext(MessagePropertiesContext);
  const isProductInStockInStore = store.instoreAvailability.available;
  const isProductInStockOnline = store.onlineAvailability.available;
  const isStoreSelected = selectedOption === instoreUniqueValue || selectedOption === onlineUniqueValue;

  function handleOnChange(e: React.ChangeEvent<HTMLInputElement>): void {
    setSelectedOption(e.currentTarget.value);
    setSelectedStore(store);
  }

  useEffect(() => {
    if (isStoreSelected) {
      setSelectedStore(store);
    }
  }, [store, selectedOption]);

  return (
    <div
      className={`reserve-collect-store 
        ${isProductInStockInStore ? 'reserve-collect-store--inStock' : 'reserve-collect-store--outOfStock'}
        ${isStoreSelected ? 'reserve-collect-store--selected' : ''}`}
    >
      <div className="reserve-collect-store__headline">
        <h5 className="reserve-collect-store__headline-title">
          <span className="reserve-collect-store__headline-title-text">{store.store.storeName}</span>
          <span className="reserve-collect-store__headline-distance">{store.store.formattedDistance}</span>
        </h5>
        <div className="reserve-collect-store__headline-status">
          <p className="reserve-collect-store__headline-status-text">
            {isProductInStockInStore
              ? formattedMessage({ id: 'reserve.and.collect.inStock.label', fallback: 'Available' })
              : formattedMessage({ id: 'reserve.and.collect.outOfStock.label', fallback: 'No longer available' })}
          </p>
        </div>
      </div>

      <address className="reserve-collect-store__address">{store.store.address.line2}</address>

      <ul className="reserve-collect-store__options reserve-collect-store__options--radio-list">
        <li
          className={`reserve-collect-store__options-item 
          ${isProductInStockInStore ? '' : 'reserve-collect-store__options-item--disabled'}
          ${selectedOption === instoreUniqueValue ? 'reserve-collect-store__options-item--checked' : ''}
          `}
        >
          <div className="radio reserve-collect-store__options-item-radio">
            <input
              type="radio"
              name="storeOptions"
              className="radio__field"
              id={instoreUniqueValue}
              value={instoreUniqueValue}
              onChange={handleOnChange}
              checked={selectedOption === instoreUniqueValue}
              disabled={!isProductInStockInStore || disabled}
            />
            <span className="radio__icon radio__icon--inverted reserve-collect-store__options-item-icon" />
          </div>
          <label htmlFor={instoreUniqueValue} className="reserve-collect-store__options-item-label">
            <span className="reserve-collect-store__options-item-label-title">
              {formattedMessage({ id: 'reserve.and.collect.reserve.label', fallback: 'Reserve' })}
            </span>
            <span className="reserve-collect-store__options-item-label-message">
              {store.instoreAvailability.message}
            </span>
            {isRingProduct && isProductInStockInStore && (
              <>
                {store.availableInstoreProduct.size && store.availableInstoreProduct.code === searchProductCode ? (
                  <span className="reserve-collect-store__options-item-label-message">
                    Size {store.availableInstoreProduct.size} available
                  </span>
                ) : (
                  <span
                    className="reserve-collect-store__options-item-label-message reserve-collect-store__options-item-label-message--warning"
                    dangerouslySetInnerHTML={{
                      __html: `Size ${store.availableInstoreProduct.size} available to view`
                    }}
                  />
                )}
              </>
            )}
          </label>
        </li>

        {!priceUponEnquiry && (
          <li
            className={`reserve-collect-store__options-item 
          ${isProductInStockOnline ? '' : 'reserve-collect-store__options-item--disabled'}
          ${selectedOption === onlineUniqueValue ? 'reserve-collect-store__options-item--checked' : ''}
          `}
          >
            <div className="radio reserve-collect-store__options-item-radio">
              <input
                type="radio"
                name="storeOptions"
                className="radio__field"
                id={onlineUniqueValue}
                value={onlineUniqueValue}
                onChange={handleOnChange}
                checked={selectedOption === onlineUniqueValue}
                disabled={!isProductInStockOnline || disabled}
              />
              <span className="radio__icon radio__icon--inverted reserve-collect-store__options-item-icon" />
            </div>

            <label htmlFor={onlineUniqueValue} className="reserve-collect-store__options-item-label">
              <span className="reserve-collect-store__options-item-label-title">
                {formattedMessage({ id: 'reserve.and.collect.buyOnline.label', fallback: 'Buy Online' })}
              </span>
              <span className="reserve-collect-store__options-item-label-message">
                {store.onlineAvailability.message}
              </span>
            </label>
          </li>
        )}
      </ul>
    </div>
  );
}

export default ReserveCollectStore;
